import { Visibility, ArticlePreviewResponse, isDcmaGraylisted, Collection, PublicCollectionResponse, UserProfileStyle } from "@authory-packages/core";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";

const CHANGE_VIEW_TRESHOLD = 700;
const SIDEBAR_LAYOUT_VIEW_TRESHOLD = 1350;

/**
 * Returns the width below which multi column views collapse into one column.
 * @param profileStyle The profile style, the sidebar leaves less room for columns
 * @returns The breakpoint in pixels
 */
export function getResponsiveBreakpoint(profileStyle?: UserProfileStyle) {
    return profileStyle === UserProfileStyle.sidebar ? SIDEBAR_LAYOUT_VIEW_TRESHOLD : CHANGE_VIEW_TRESHOLD;
}

export enum ItemUrlContext {
    profile = "profile",
    collection = "collection",
    content = "content"
}

export const getItemUrl = (
    item: ArticlePreviewResponse,
    authorSlug: string,
    context: ItemUrlContext,
    smartShareUrl?: string,
    isMainDomain: boolean = true,
    collection?: Collection | PublicCollectionResponse
) => {
    const isDMCAProtected = isDcmaGraylisted(item.originalUrl);

    // for DMCA protected articles, always return original URL as they can't be published on authory
    if (isDMCAProtected && item.originalUrl) return item.originalUrl;

    // if the effective vis is preview, then we will never link to authory copy
    if (item.effectiveVisibility === Visibility.Preview && item.originalUrl) return item.originalUrl;

    const computedAuthorSlug = isMainDomain ? authorSlug : "";
    const computedItemSlug = item?.canonicalSlug || "";
    const computedCollectionSlug = collection?.canonicalSlug || "";

    let url = "";

    if (!!smartShareUrl) {
        if (context === ItemUrlContext.profile) {
            url = APPLICATION_ROUTES.SMART_SHARE_PROFILE_CONTENT_ITEM_PAGE.replace("[author]", computedAuthorSlug)
                .replace("[contentItemAccessKey]", computedItemSlug)
                .replace("[smartShareURL]", smartShareUrl);
        } else if (context === ItemUrlContext.collection) {
            url = APPLICATION_ROUTES.SMART_SHARE_COLLECTION_CONTENT_ITEM_PAGE.replace("[author]", computedAuthorSlug)
                .replace("[contentItemAccessKey]", computedItemSlug)
                .replace("[smartShareURL]", smartShareUrl);
        }
    } else {
        if (context === ItemUrlContext.profile) {
            url = APPLICATION_ROUTES.PROFILE_CONTENT_ITEM_PAGE.replace("[author]", computedAuthorSlug).replace(
                "[contentItemAccessKey]",
                computedItemSlug
            );
        } else if (context === ItemUrlContext.collection) {
            url = APPLICATION_ROUTES.COLLECTION_CONTENT_ITEM_PAGE.replace("[author]", computedAuthorSlug)
                .replace("[collectionAccessKey]", computedCollectionSlug)
                .replace("[contentItemAccessKey]", computedItemSlug);
        }
    }

    const contentItemAccessToken = collection?.overrideContentVisibility === Visibility.Public ? `?c=${collection.canonicalSlug}` : "";

    url = `${url}${contentItemAccessToken}`;

    return url;
};

export const getItemTarget = (item: ArticlePreviewResponse, isPrivateView?: boolean) => {
    const isDMCAProtected = isDcmaGraylisted(item.originalUrl);

    // for DMCA protected articles, always open a new tab
    if (isDMCAProtected && item.originalUrl) return "_blank";

    return item.effectiveVisibility === Visibility.Preview && !isPrivateView ? "_blank" : undefined;
};
